import * as React from "react";
import "./inputWithIcon.scss";
import {isNullOrUndefined} from "util";
import IconWrapper from "../Icon/IconWrapper";
import {Icon, IconVariant} from "../Icon/Icon";
import {isPhoneScreen} from "../../../../utils/Utils";
import classNames = require("classnames");

interface IProps {
	/**
	 * Input field type attribute.
	 */
	type: "password" | "text" | "number" | "email";
	/**
	 * Input value.
	 */
	value: string | number;
	/**
	 * Placeholder string.
	 */
	placeholder?: string;
	/**
	 * Indicates if field has focus.
	 */
	focus?: boolean;
	/**
	 * Indicates if field is disabled.
	 */
	disabled?: boolean;
	/**
	 * Indicates if input field is readonly.
	 */
	readonly?: boolean;
	/**
	 * Input field on blur callback.
	 */
	onBlur?: (evt: React.FocusEvent<HTMLInputElement>) => void;
	/**
	 * On input focus callback.
	 */
	onFocus?: (evt: React.FocusEvent<HTMLInputElement>) => void;
	/**
	 * Input click callback
	 */
	onClick?: (evt: React.MouseEvent<HTMLInputElement>) => void;
	/**
	 * Input on change callback.
	 */
	onChange?: (evt: React.ChangeEvent<HTMLInputElement>) => void;
	/**
	 * On pressed 'enter' button.
	 */
	onConfirm?: (evt: React.KeyboardEvent<HTMLInputElement>) => void;
	/**
	 * Prepend Icon name.
	 */
	prependIcon?: string;
	/**
	 * Prepend icon click callback.
	 * @param {React.MouseEvent<SVGSVGElement>} evt
	 */
	onPrependIconClick?: (evt: React.MouseEvent<SVGSVGElement>) => void;
	/**
	 * Append icon name.
	 */
	appendIcon?: string;
	/**
	 * Append icon click callback.
	 * @param {React.MouseEvent<SVGSVGElement>} evt
	 */
	onAppendIconClick?: (evt: React.MouseEvent<SVGSVGElement>) => void;
	/**
	 * Indicates if input field has clear icon.
	 */
	clearable?: boolean;
	/**
	 * Clear icon click callback
	 */
	onClearClick?: () => void
	/**
	 * Hide blinking input cursor and display pointer
	 */
	inputWithoutCursor?: boolean
	/**
	 * Set icon color variant
	 */
	iconVariant?: IconVariant
	/**
	 * Custom class for append icon container.
	 */
	iconAppendCustomClass?: string;
}

export default class InputWithIcon extends React.Component<IProps, {}> {

	public static defaultProps: Partial<IProps> = {
		iconVariant: "blue",
		iconAppendCustomClass: ""
	};

	inputRef: HTMLInputElement;

	constructor(props: IProps) {
		super(props);
		this.onChange = this.onChange.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.renderPrependIcon = this.renderPrependIcon.bind(this);
		this.renderAppendIcon = this.renderAppendIcon.bind(this);
		this.renderClear = this.renderClear.bind(this);
	}

	componentDidUpdate(prevProps: IProps) {
		if (prevProps.focus === false && this.props.focus === true) {
			this.inputRef.focus();
		}
	}

	componentDidMount(): void {
		if (this.props.focus && !isPhoneScreen()) {
			this.inputRef.focus();
			this.inputRef.setSelectionRange(this.inputRef.value.length, this.inputRef.value.length);
		}
	}

	focus = () => this.inputRef.focus();

	render() {

		const styleFromProps = this.props.disabled ? {cursor: "not-allowed"} : {};
		const classFromProps = classNames({
			"a-input-with-icon": true,
			"is-disabled": this.props.disabled,
			"is-readonly": this.props.readonly,
			"input-without-cursor": this.props.inputWithoutCursor
		});

		return (
			<div className={classFromProps}>
				{this.renderPrependIcon()}
				{this.renderAppendIcon()}
				<input ref={(input) => this.inputRef = input}
					   style={styleFromProps}
					   onKeyDown={this.onKeyDown}
					   onChange={this.onChange}
					   value={this.props.value}
					   type={this.props.type}
					   onFocus={this.props.onFocus}
					   onBlur={this.props.onBlur}
					   onClick={this.props.onClick}
					   placeholder={this.props.placeholder}
					   disabled={this.props.disabled}
					   readOnly={this.props.readonly}/>
			</div>
		);
	}

	/**
	 * On input change.
	 * @param {React.ChangeEvent<HTMLInputElement>} event Input event.
	 */
	private onChange(event: React.ChangeEvent<HTMLInputElement>) {
		if (!this.props.disabled && !this.props.readonly && !isNullOrUndefined(this.props.onChange)) {
			this.props.onChange(event);
		}
	}

	/**
	 * On pressed 'enter' key.
	 * @param {React.KeyboardEvent<HTMLInputElement>} event
	 */
	private onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		if (event.key === "Enter" && !isNullOrUndefined(this.props.onConfirm)) {
			this.props.onConfirm(event);
		}
	}

	/**
	 * Indicates if icon should be grayed.
	 * @return {boolean | undefined}
	 */
	private isIconGray() {
		return !Boolean(this.props.value) || this.props.readonly || this.props.disabled;
	}

	/**
	 * Render clear icon
	 * @returns {JSX.Element}
	 */
	renderClear() {
		return (
			!this.isIconGray() && this.props.clearable &&
			<IconWrapper icon={Icon.X}
						 size={18}
						 variant={"blue"}
						 onClick={this.props.onClearClick}/>)
	}

	/**
	 * Get icon variant.
	 * @return {IconVariant}
	 */
	private getIconVariant(): IconVariant {
		return this.isIconGray() ? "gray" : this.props.iconVariant;
	}

	/**
	 * Render prepend icon.
	 * @return {JSX.Element}
	 */
	private renderPrependIcon() {
		if (!isNullOrUndefined(this.props.prependIcon)) {
			return (
				<span className={"a-input-with-icon_icon-prepend"}>
					<IconWrapper icon={this.props.prependIcon}
								 disabled={this.props.disabled}
								 size={18}
								 variant={this.getIconVariant()}
								 onClick={this.props.disabled ? () => {} : this.props.onPrependIconClick}/>
				</span>
			);
		}
	}

	/**
	 * Render append icon.
	 * @return {JSX.Element}
	 */
	private renderAppendIcon() {
		if (!isNullOrUndefined(this.props.appendIcon)) {
			return (
				<span className={`a-input-with-icon_icon-append ${this.props.iconAppendCustomClass}`}>
					{this.renderClear()}
					<IconWrapper icon={this.props.appendIcon}
								 disabled={this.props.disabled}
								 size={18}
								 variant={this.getIconVariant()}
								 onClick={this.props.disabled ? null : this.props.onAppendIconClick}/>
				</span>
			);
		}
		else if (this.props.clearable) {
			return (
				<span className={"a-input-with-icon_icon-append"}>
					{this.renderClear()}
				</span>
			)
		}
	}

}
